import { useEffect, useState } from 'react';

/**
 * Object URL for a piece of text, revoked when the text changes or the
 * caller unmounts.
 */
export function useBlobUrl(text: string | null | undefined, type: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!text) {
      setUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    try {
      objectUrl = URL.createObjectURL(new Blob([text], { type }));
      setUrl(objectUrl);
    } catch {
      setUrl(null);
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [text, type]);
  return url;
}

/**
 * Subtitles track for a <video>. The WebVTT is fetched separately (CORS) and
 * served from a same-origin blob URL, so the video element needs no
 * crossOrigin attribute. Off by default; viewers switch it on with the
 * native CC control.
 */
export function CaptionsTrack({ vttText, language }: { vttText?: string | null; language?: string | null }) {
  const url = useBlobUrl(vttText, 'text/vtt');
  if (!url) return null;
  return <track kind="subtitles" srcLang={language || 'en'} label="Captions" src={url} />;
}

/** Save text as a file through a temporary object URL. */
export function saveTextFile(text: string, fileName: string) {
  try {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  } catch {
    /* nothing sensible to do */
  }
}
